import { Accordion, Button, Modal, Select, TagsInput, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  buildPresetPermissions,
  PERMISSION_SUBJECTS,
  PermissionsEditor,
  PermissionsState,
  usePermissionSubjectLabels,
} from 'src/Components/Admin/PermissionsEditor';
import { useAssetInviteUser } from 'src/Hooks/Mutations/useAssetInviteUser';
import {
  CreateInvitationsDtoPresetEnum,
  PermissionEntry,
  PermissionEntryLevelEnum,
  UpdateMemberPermissionsDtoPresetEnum,
} from 'src/Services/API/generated';

import styles from './InviteByEmailModal.module.scss';
import { Props } from './InviteByEmailModal.types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_PRESET = CreateInvitationsDtoPresetEnum.Viewer;

const InviteByEmailModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const subjectLabels = usePermissionSubjectLabels();

  const [emails, setEmails] = useState<string[]>([]);
  const [preset, setPreset] = useState<CreateInvitationsDtoPresetEnum>(DEFAULT_PRESET);
  const [permissions, setPermissions] = useState<PermissionsState>(() =>
    buildPresetPermissions(UpdateMemberPermissionsDtoPresetEnum.Viewer)
  );

  const resetAndClose = () => {
    setEmails([]);
    setPreset(DEFAULT_PRESET);
    setPermissions(buildPresetPermissions(UpdateMemberPermissionsDtoPresetEnum.Viewer));
    onClose();
  };

  const { mutate, isLoading } = useAssetInviteUser(undefined, {
    onSuccess: () => {
      resetAndClose();
    },
  });

  const handlePresetChange = (value: string | null) => {
    if (!value) return;
    const nextPreset = value as CreateInvitationsDtoPresetEnum;
    setPreset(nextPreset);
    setPermissions(
      buildPresetPermissions(nextPreset as unknown as UpdateMemberPermissionsDtoPresetEnum)
    );
  };

  const invalidEmails = emails.filter(email => !EMAIL_REGEX.test(email));

  const { visibleSubjects, hiddenSubjects } = useMemo(() => {
    const visible: string[] = [];
    const hidden: string[] = [];
    for (const subject of PERMISSION_SUBJECTS) {
      if (permissions[subject] === PermissionEntryLevelEnum.None) {
        hidden.push(subjectLabels[subject]);
      } else {
        visible.push(subjectLabels[subject]);
      }
    }
    return { visibleSubjects: visible, hiddenSubjects: hidden };
  }, [permissions, subjectLabels]);

  const handleSubmit = () => {
    const permissionEntries: PermissionEntry[] = PERMISSION_SUBJECTS.map(subject => ({
      subject,
      level: permissions[subject],
    }));
    mutate({ emails, preset, permissions: permissionEntries });
  };

  return (
    <Modal
      opened={open}
      onClose={resetAndClose}
      title={
        <div className={styles.title}>{t('admin.inviteByEmail.title', 'Inviter par email')}</div>
      }
      size="lg"
    >
      <div className={styles.content}>
        <div className={styles.emailsField}>
          <TagsInput
            label={t('admin.inviteByEmail.emailsLabel', 'Adresses email')}
            placeholder={t('admin.inviteByEmail.emailsPlaceholder', 'email@exemple.com')}
            value={emails}
            onChange={setEmails}
            splitChars={[',', ' ', ';']}
            error={
              invalidEmails.length > 0
                ? t('admin.inviteByEmail.invalidEmails', 'Adresse invalide : {{emails}}', {
                    emails: invalidEmails.join(', '),
                  })
                : undefined
            }
          />
          <Text size="xs" c="dimmed">
            {t('admin.inviteByEmail.emailsHint', 'Appuyez sur Entrée après chaque email')}
          </Text>
        </div>

        <Select
          label={t('admin.inviteByEmail.roleLabel', 'Rôle')}
          allowDeselect={false}
          value={preset}
          onChange={handlePresetChange}
          data={[
            {
              value: CreateInvitationsDtoPresetEnum.Viewer,
              label: t('permissions.presets.viewer', 'Visiteur'),
            },
            {
              value: CreateInvitationsDtoPresetEnum.Contributor,
              label: t('permissions.presets.contributor', 'Contributeur'),
            },
            {
              value: CreateInvitationsDtoPresetEnum.Manager,
              label: t('permissions.presets.manager', 'Gestionnaire'),
            },
          ]}
        />

        <div className={styles.preview}>
          <Text size="sm">
            {t('admin.inviteByEmail.previewVisible', 'Cette personne verra : {{modules}}.', {
              modules: visibleSubjects.join(', '),
            })}
          </Text>
          {hiddenSubjects.length > 0 && (
            <Text size="sm" c="dimmed">
              {t('admin.inviteByEmail.previewHidden', 'Elle ne verra pas : {{modules}}.', {
                modules: hiddenSubjects.join(', '),
              })}
            </Text>
          )}
        </div>

        <Accordion variant="contained">
          <Accordion.Item value="detailed-permissions">
            <Accordion.Control>
              {t('permissions.detailed.label', 'Permissions détaillées')}
            </Accordion.Control>
            <Accordion.Panel>
              <PermissionsEditor value={permissions} onChange={setPermissions} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <div className={styles.buttons}>
          <Button variant="light" onClick={resetAndClose} disabled={isLoading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={emails.length === 0 || invalidEmails.length > 0}
          >
            {t('admin.inviteByEmail.submit', "Envoyer l'invitation")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default InviteByEmailModal;
